import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Toggle } from "react-toggle-component";

import BlueButton from "src/Components/Common/bluebutton";
import CancelButton from "src/Components/Common/cancelbutton";
import RoleService from "src/Services/API/role.service";
import UserService from "src/Services/API/user.service";
import UserFormPermissionService from "src/Services/API/userformpermission.service";
import RoleDropDown from "./roledropdown";
import ConfirmDialog from "src/Common/confirmdialog";
import { formatDate, getImageUrl } from "src/Common/common";
import InviteModal from "../Invite/InviteModal/invitemodal";
import InviteService from "src/Services/API/invite.service";
import BranchService from "src/Services/API/branch.service";
import { setBranch } from "src/Redux/Slices/BranchSlice";
import BranchDropDown from "./branchdropdown";
import { handleError, downloadFile, isSuperAdmin, API_URL, formatUSPhone, isValidUSPhone, toE164Phone } from "src/Common/function";
import TechnicianStatus from "src/Pages/WO/Components/TechnicianStatus";
import {
  connectWoTechStatusSocket,
  disconnectWoTechStatusSocket,
} from "src/Services/socket/woTechStatusSocket";

function Manage({ branch }) {
  const navigate = useNavigate();
  const currentUser = UserService.getCurrentUser();
  const superAdmin = isSuperAdmin();
  const [roleArray, setRoleArray] = useState([]);
  const [userArray, setUserArray] = useState([]);
  const [branchArray, setBranchArray] = useState([]);
  const [index, setIndex] = useState(0);
  const [isDeleteConfirmDialogShown, setIsDeleteConfirmDialogShown] = useState(false);
  const [roleChangedUserArray, setRoleChangedUserArray] = useState([]);
  const [isDownloadExcelLoading, setIsDownloadExcelLoading] = useState(false);
  const [editingPhoneUserId, setEditingPhoneUserId] = useState(null);
  const [editingPhoneValue, setEditingPhoneValue] = useState("");
  const [formEnabledMap, setFormEnabledMap] = useState({});

  useEffect(() => {
    BranchService.getAll()
      .then((response) => {
        setBranchArray(response.data);
      })
      .catch((error) => {
        toast("An error occured");
      });
  }, []);

  useEffect(() => {
    RoleService.getAll()
      .then((response) => {
        console.log("Role list: ", response);
        if (response.status == 200) {
          setRoleArray(response.data);
        }
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });
    getAllUsers();
  }, [branch]);

  // Keep each user's "active WO" column live without a page refresh.
  // The WoTechStatusGateway emits `wo-tech-status-changed` whenever a
  // technician flips their status (Enroute / On Site / Getting Parts /
  // Checked Out), including the cascade when starting a new WO auto-checks
  // them out of their previous one.
  useEffect(() => {
    const ACTIVE_STATUSES = [2, 3, 4];
    const CHECKED_OUT = 1;

    const socket = connectWoTechStatusSocket();

    const joinTeamRoom = () => {
      socket.emit("wo-tech-status:team:join");
    };

    const handleTechStatusChanged = (payload) => {
      if (!payload) return;
      const userId = Number(payload.userId);
      const woId = Number(payload.woId);
      const techStatus = Number(payload.techStatus);
      if (!userId || !woId) return;

      setUserArray((prev) =>
        prev.map((user) => {
          if (Number(user.id) !== userId) return user;

          const existing = user.activeTechs ?? [];
          let next;

          if (ACTIVE_STATUSES.includes(techStatus)) {
            const found = existing.some((t) => Number(t.woId) === woId);
            const entry = {
              id: payload.technicianId,
              techStatus,
              woId,
              woNumber: payload.woNumber,
            };
            next = found
              ? existing.map((t) =>
                Number(t.woId) === woId ? { ...t, ...entry } : t
              )
              : [...existing, entry];
          } else if (techStatus === CHECKED_OUT) {
            next = existing.filter((t) => Number(t.woId) !== woId);
          } else {
            next = existing;
          }

          return { ...user, activeTechs: next };
        })
      );
    };

    socket.on("connect", joinTeamRoom);
    socket.on("wo-tech-status-changed", handleTechStatusChanged);

    if (socket.connected) {
      joinTeamRoom();
    }

    return () => {
      socket.off("connect", joinTeamRoom);
      socket.off("wo-tech-status-changed", handleTechStatusChanged);
      socket.emit("wo-tech-status:team:leave");
      disconnectWoTechStatusSocket();
    };
  }, []);

  const getAllUsers = () => {
    UserService.getAll(branch)
      .then((response) => {
        if (response.status == 200) {
          let users = response.data.filter((user) => user.isEnabled === true);
          for (let i = 0; i < users.length; i++) {
            const date = new Date(users[i].createdAt);
            users[i].formattedCreatedAt = formatDate(date);
          }
          setUserArray(users);
          loadFormPermissions(users.map((u) => u.id));
        }
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });
  };

  // Users without an explicit row are treated as "form disabled" (default OFF).
  const loadFormPermissions = (userIds) => {
    if (!userIds || userIds.length === 0) {
      setFormEnabledMap({});
      return;
    }
    UserFormPermissionService.getMany(userIds)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const map = {};
          userIds.forEach((id) => {
            map[id] = false;
          });
          (response.data || []).forEach((row) => {
            map[row.userId] = row.isFormEnabled;
          });
          setFormEnabledMap(map);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const toggleFormEnabled = (user, isFormEnabled) => {
    setFormEnabledMap((prev) => ({ ...prev, [user.id]: isFormEnabled }));
    UserFormPermissionService.set(user.id, isFormEnabled)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast(
            isFormEnabled
              ? `Form enabled for ${user.name}`
              : `Form disabled for ${user.name}`,
          );
        }
      })
      .catch((error) => {
        setFormEnabledMap((prev) => ({ ...prev, [user.id]: !isFormEnabled }));
        handleError(error);
      });
  };

  const deleteUser = () => {
    UserService.deleteUser(userArray[index].id)
      .then((response) => {
        if (response.status == 201) {
          toast("User archived successfully");
          getAllUsers();
        }
        setIsDeleteConfirmDialogShown(false);
      })
      .catch((error) => {
        setIsDeleteConfirmDialogShown(false);

        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });
  };

  const roleChange = (user, newRole) => {
    user.roles = [newRole];

    UserService.updateUser(user)
      .then((response) => {
        if (response.status == 201) {
          toast("User role changed successfully");
          if (user.id === currentUser.id) {
            UserService.saveCurrentUser(response.data);
          }
        } else {
          toast("Something went wrong!");
        }
      })
      .catch((error) => {
        if (error.response) {
          toast(error.response.data.message);
        } else {
          toast("An error occured");
        }
      });

    // user.roles = [newRole];
    // let tempArray = [...roleChangedUserArray];
    // tempArray.push(user);
    // setRoleChangedUserArray(tempArray);
  };

  // const saveChanges = () => {
  //   console.log("roleChangedUserArray: ", roleChangedUserArray);
  //   UserService.updateUsers(roleChangedUserArray)
  //     .then((response) => {
  //       if (response.status == 201 && response.data == true) {
  //         toast("Saved successfully");
  //         navigate(-1);
  //       } else {
  //         toast("Something went wrong!");
  //       }
  //     })
  //     .catch((error) => {
  //       if (error.response) {
  //         toast(error.response.data.message);
  //       } else {
  //         toast("An error occured");
  //       }
  //     });
  // };

  const branchChange = (user, branch, checked) => {
    let userData;
    if (checked) {
      //add branch
      userData = { ...user, branches: [...user.branches, branch] };
    } else {
      //remove branch
      const branchArray = user.branches.filter((item) => item.id !== branch.id);
      userData = { ...user, branches: branchArray };
    }

    console.log("userData: ", userData);

    UserService.updateUser(userData)
      .then((response) => {
        if (response.status == 201) {
          getAllUsers();
          const message = checked ? `${branch.name} branch has been added to ${user.name} successfully` : `${branch.name} branch has been removed from ${user.name} successfully`
          toast(message)
        }
      })
      .catch((error) => {
        getAllUsers();
        handleError(error);
      });
  };

  const downloadExcel = async () => {
    setIsDownloadExcelLoading(true);
    try {
      let response = await UserService.generateExcelForTeamManage(branch);
      console.log("🚀 ~ downloadExcel ~ response:", response);
      if (response.status == 200) {
        await downloadFile(`${API_URL}/${currentUser.company}/reports/${response.data}`, response.data);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsDownloadExcelLoading(false);
    }
  };

  const savePhone = (user) => {
    const trimmed = editingPhoneValue.trim();
    if (trimmed === (user.phone || "")) {
      setEditingPhoneUserId(null);
      return;
    }
    if (trimmed !== "" && !isValidUSPhone(trimmed)) {
      return;
    }
    const normalized = trimmed === "" ? "" : toE164Phone(trimmed);
    const userData = { ...user, phone: normalized };
    UserService.updateUser(userData)
      .then((response) => {
        if (response.status == 201) {
          toast("Phone number updated successfully");
          getAllUsers();
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setEditingPhoneUserId(null);
      });
  };

  return (
    <div>
      <ConfirmDialog
        title="ARCHIVE USER"
        description="Are you sure you want to archive this user?"
        shown={isDeleteConfirmDialogShown}
        onConfirmClicked={deleteUser}
        onCancelClicked={() => setIsDeleteConfirmDialogShown(false)}
      />
      <div className="w-full flex items-center gap-2 justify-end">
        <BlueButton title="Download Excel" onClicked={downloadExcel} isLoading={isDownloadExcelLoading} />
      </div>
      <table className="w-full mt-3">
        <thead>
          <tr className="border-t-[1px] border-b-[1px] border-borderColor">
            <th className="border-r-[1px] border-borderColor w-[50px]">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                <span>No.</span>
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                <span>Name</span>
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                <span>Email</span>
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor">
              <div className="w-min-[100px] font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                <span>Status</span>
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor">
              <div className="w-min-[100px] font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                <span>WO #</span>
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor w-[120px]">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-4 h-[40px] text-titleColor">
                <span>Phone</span>
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor w-[200px]">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center pl-4 h-[40px] text-titleColor">
                Role
              </div>
            </th>
            <th className="border-r-[1px] border-borderColor w-[250px]">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center pl-4 h-[40px] text-titleColor">
                Branch
              </div>
            </th>
            {superAdmin && (
              <th className="border-r-[1px] border-borderColor w-[80px]">
                <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center justify-center h-[40px] text-titleColor">
                  Form
                </div>
              </th>
            )}
            <th className="w-[170px]">
              <div className="font-OpenSansRegular font-[700] text-[12px] flex items-center px-2 h-[40px] text-titleColor">
                Added
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {userArray.map((user, i) => (
            <tr key={i} className="border-b-[1px] border-borderColor h-[40px]">
              <td className="border-r-[1px]  border-borderColor">
                <div className="font-OpenSansRegular px-[15px] py-[5px] text-[11px] font-[400] col-span-1">
                  {i + 1}
                </div>
              </td>
              <td className="border-r-[1px]  border-borderColor">
                <div className="font-OpenSansRegular px-[15px] py-[5px] text-[11px] font-[400] col-span-1">
                  {user.name}
                </div>
              </td>
              <td className="border-r-[1px]  border-borderColor">
                <div className="font-OpenSansRegular px-[15px] py-[5px] text-[11px] font-[400] col-span-1">
                  {user.email}
                </div>
              </td>
              <td className="border-r-[1px]  border-borderColor">
                <div className="font-OpenSansRegular px-[5px] py-[5px] text-[11px] font-[400] col-span-1 flex flex-col gap-[4px] items-center">
                  {(user.activeTechs ?? []).map((tech) => (
                    <TechnicianStatus key={tech.id} status={tech.techStatus} />
                  ))}
                </div>
              </td>
              <td className="border-r-[1px]  border-borderColor">
                <div className="font-OpenSansRegular px-[5px] py-[5px] text-[11px] font-[400] col-span-1 flex flex-col items-center gap-[4px]">
                  {(user.activeTechs ?? []).map((tech) => (
                    <a
                      key={tech.id}
                      href={`/work/${tech.woId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 cursor-pointer hover:underline"
                    >
                      {tech.woNumber}
                    </a>
                  ))}
                </div>
              </td>
              <td className="border-r-[1px]  border-borderColor relative">
                {editingPhoneUserId === user.id ? (
                  <div className="flex items-center">
                    <input
                      autoFocus
                      className={`font-OpenSansRegular px-[15px] py-[5px] text-[11px] font-[400] w-full outline-none bg-transparent border-b ${editingPhoneValue && !isValidUSPhone(editingPhoneValue) ? "border-red-500" : "border-blue-500"}`}
                      value={editingPhoneValue}
                      placeholder="(555) 123-4567"
                      maxLength={14}
                      onChange={(e) => setEditingPhoneValue(formatUSPhone(e.target.value))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") savePhone(user);
                        if (e.key === "Escape") setEditingPhoneUserId(null);
                      }}
                    />
                    <EditButton clicked={() => savePhone(user)} isEditing={true} />
                  </div>
                ) : (
                  <div className="flex items-center">
                    <div className="font-OpenSansRegular px-[15px] py-[5px] text-[11px] font-[400] col-span-1">
                      {user.phone ? formatUSPhone(user.phone) : "—"}
                    </div>
                    {superAdmin && (
                      <EditButton
                        clicked={() => {
                          setEditingPhoneUserId(user.id);
                          setEditingPhoneValue(formatUSPhone(user.phone || ""));
                        }}
                      />
                    )}
                  </div>
                )}
              </td>
              <td className="border-r-[1px] border-borderColor relative">
                <RoleDropDown user={user} roles={roleArray} roleClicked={roleChange} />
              </td>
              <td className="border-r-[1px] border-borderColor relative">
                <BranchDropDown user={user} branches={branchArray} onChange={branchChange} />
              </td>
              {superAdmin && (
                <td className="border-r-[1px] border-borderColor">
                  <div className="flex items-center justify-center px-[15px] py-[5px]">
                    <Toggle
                      controlled
                      leftBackgroundColor="#E6E7EC"
                      rightBackgroundColor="#0065FD"
                      borderColor="none"
                      knobColor={formEnabledMap[user.id] ? "white" : "#0065FD"}
                      name={`form-toggle-${user.id}`}
                      checked={!!formEnabledMap[user.id]}
                      onToggle={(e) => toggleFormEnabled(user, e.target.checked)}
                    />
                  </div>
                </td>
              )}

              <td>
                <div className="font-OpenSansRegular px-[15px] py-[5px] text-[11px] font-[400] col-span-1 flex items-center justify-between">
                  <span>{user.formattedCreatedAt}</span>
                  <img
                    onClick={() => {
                      setIsDeleteConfirmDialogShown(!isDeleteConfirmDialogShown);
                      setIndex(i);
                    }}
                    src={getImageUrl("/images/common/trash.png")}
                    className="cursor-pointer"
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Manage;

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  return (
    isEditing ? (
      <div onClick={clicked} className="w-[18px] absolute right-[5%] top-[12px] cursor-pointer">
        <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" />
      </div>
    ) : (
      <div
        onClick={isDisabled ? undefined : clicked}
        className={`w-[18px] absolute right-[5%] top-[12px] ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
      >
        <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" />
      </div>
    )
  );
};